/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { getSpaceSubmodulePath } from '@coze-studio/abr-brand';
import { I18n } from '@coze-arch/i18n';

import { type DevelopMetrics } from '../../hooks/use-develop-metrics';
import { ABR_APP_STORE_PATH, ABR_ONTOLOGY_PORTAL_URL } from '../../constants';
import { HeroResourceStrip } from './hero-resource-strip';
import { HeroCreateAgentBento } from './hero-create-agent-bento';

import styles from './index.module.less';

export interface HeroDashboardProps {
  spaceId?: string;
  spaceName?: string;
  metrics: DevelopMetrics;
  shortcutLabel?: string;
  onQuickOpen?: () => void;
  onCreateAgent: () => void;
}

const METRIC_PLACEHOLDER = '—';
const PERCENT_BASE = 100;

export const HeroDashboard: FC<HeroDashboardProps> = ({
  spaceId,
  spaceName,
  metrics,
  shortcutLabel = '⌘K',
  onQuickOpen,
  onCreateAgent,
}) => {
  const subtitle = spaceName
    ? I18n.t('abr_home_hero_subtitle', { spaceName })
    : I18n.t('abr_home_hero_subtitle_fallback');

  const libraryPath = spaceId
    ? getSpaceSubmodulePath(spaceId, 'library')
    : undefined;

  const draftBarWidth = metrics.agentCount
    ? Math.round((metrics.draftAgentCount / metrics.agentCount) * PERCENT_BASE)
    : 0;

  const publishedAppsLabel = metrics.publishedAppLoading
    ? METRIC_PLACEHOLDER
    : String(metrics.publishedAppCount);

  return (
    <div className={styles.heroBlock}>
      <div className={styles.heroGridLines} />
      <div className={`${styles.heroMesh} abr-tech-hero-mesh`} />
      <div className={`${styles.heroOrbit} abr-tech-hero-orbit`} />
      <div className={styles.heroInner}>
        <div className={styles.heroLive}>
          <span className={`${styles.pulse} abr-tech-pulse`} />
          {I18n.t('abr_home_hero_live_status')}
        </div>
        <div className={styles.heroTop}>
          <div>
            <h2 className={styles.title}>{I18n.t('abr_home_workbench')}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          <button
            type="button"
            className={styles.cmdK}
            onClick={onQuickOpen}
            data-testid="abr-quick-open-trigger"
          >
            <span aria-hidden>🔍</span>
            <span>{I18n.t('abr_home_quick_open')}</span>
            <kbd>{shortcutLabel}</kbd>
          </button>
        </div>
        <div className={styles.heroKpiLayout}>
          <div className={styles.heroMetrics}>
            <div
              className={`${styles.heroMetric} ${styles.highlight} abr-tech-metric-highlight`}
            >
              <div className={styles.num}>{metrics.agentCount}</div>
              <div className={styles.lbl}>
                {I18n.t('abr_home_metric_agent')}
              </div>
              <div className={styles.metricBar}>
                <span style={{ width: `${draftBarWidth}%` }} />
              </div>
              <div className={styles.sub}>
                {I18n.t('abr_home_metric_agent_draft', {
                  count: metrics.draftAgentCount,
                })}
              </div>
            </div>
            <div className={styles.heroMetric}>
              <div className={styles.num}>{metrics.ontologyAppCount}</div>
              <div className={styles.lbl}>
                {I18n.t('abr_home_metric_ontology')}
              </div>
              <div className={styles.sub}>
                <a
                  href={ABR_ONTOLOGY_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.manageLink}
                >
                  {I18n.t('abr_home_manage_ontology')}
                </a>
              </div>
            </div>
            <a
              href={ABR_APP_STORE_PATH}
              className={`${styles.heroMetric} ${styles.heroMetricLink}`}
              data-testid="abr-metric-published-apps"
            >
              <div
                className={`${styles.num} ${
                  metrics.publishedAppLoading ? styles.numLoading : ''
                }`}
              >
                {publishedAppsLabel}
              </div>
              <div className={styles.lbl}>
                {I18n.t('abr_home_metric_published_apps')}
              </div>
              <div className={styles.sub}>
                <span className={styles.manageLink}>
                  {I18n.t('abr_home_browse_app_store')}
                </span>
              </div>
            </a>
          </div>
          <div className={styles.heroActionRow}>
            <HeroCreateAgentBento onClick={onCreateAgent} />
            <HeroResourceStrip
              libraryPath={libraryPath}
              libraryResources={metrics.libraryResources}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
